import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useShopTheme } from '@/shared/theme';
import { useIsMobile } from '@/shared/lib/react';

import { ArrowBack } from '@mui/icons-material';

interface FullscreenDialogLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  onExit: () => void;
  headerActions?: React.ReactNode;
}

export const FullscreenDialogLayout: React.FC<FullscreenDialogLayoutProps> = ({
  children,
  title,
  subtitle,
  onExit,
  headerActions,
}) => {
  const { actualTheme } = useShopTheme();
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: actualTheme === 'dark' ? '#121212' : '#ffffff',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile && headerActions ? 'column' : 'row',
          alignItems: isMobile && headerActions ? 'stretch' : 'center',
          justifyContent: 'space-between',
          p: isMobile ? 1.5 : 2,
          gap: isMobile && headerActions ? 1 : 0,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 1.5 : 2,
            minWidth: 0,
            flex: 1,
          }}
        >
          <IconButton
            onClick={onExit}
            aria-label="Back"
            size={isMobile ? 'medium' : 'small'}
            sx={{
              minWidth: isMobile ? 44 : 'auto',
              minHeight: isMobile ? 44 : 'auto',
            }}
          >
            <ArrowBack sx={{ fontSize: isMobile ? 24 : 20 }} />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              component="h1"
              sx={{
                mb: subtitle ? 0.5 : 0,
                fontSize: isMobile ? '1.1rem' : undefined,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? '0.875rem' : undefined,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {headerActions && (
          <Box
            sx={{
              display: 'flex',
              gap: isMobile ? 0.5 : 1,
              justifyContent: isMobile ? 'flex-end' : 'center',
              flexShrink: 0,
            }}
          >
            {headerActions}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: actualTheme === 'dark' ? '#1a1a1a' : '#f5f5f5',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
